import React, { useEffect, useMemo, useState } from 'react';
import { X, UserPlus, Pencil, Save, Trash2, KeyRound, Users, Shield, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const emptyForm = {
  username: '',
  email: '',
  password: '',
  display_name: '',
  role: 'viewer',
};

export default function ManageUsersModal({ onClose }) {
  const { canManageUsers } = useAuth();
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({ total: 0, admin: 0, manager: 0, viewer: 0 });
  const [activeTab, setActiveTab] = useState('members');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [query, setQuery] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getUsers();
      setUsers(data.users || []);
      setCounts(data.counts || { total: 0, admin: 0, manager: 0, viewer: 0 });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return users;
    return users.filter((user) =>
      [user.display_name, user.username, user.email, user.role].filter(Boolean).some((value) => value.toLowerCase().includes(keyword))
    );
  }, [users, query]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.createUser(form);
      setMessage(`Đã tạo user "${form.display_name}" thành công.`);
      setForm(emptyForm);
      fetchUsers();
      setActiveTab('members');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (user) => {
    setEditingId(user.id);
    setEditingDraft({
      username: user.username,
      email: user.email,
      display_name: user.display_name,
      role: user.role,
    });
  };

  const handleSaveEdit = async (id) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.updateUser(id, editingDraft);
      setMessage('Đã cập nhật user thành công.');
      setEditingId(null);
      setEditingDraft(null);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (user) => {
    const newPassword = window.prompt(`Nhập mật khẩu mới cho ${user.display_name}:`);
    if (!newPassword) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.resetUserPassword(user.id, newPassword);
      setMessage(`Đã đổi mật khẩu cho ${user.display_name}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Bạn có chắc muốn xoá user "${user.display_name}"?`)) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.deleteUser(user.id);
      setMessage(`Đã xoá user ${user.display_name}.`);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-xl" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Quản lý User</div>
          <button className="btn-icon" onClick={onClose} type="button"><X size={18} /></button>
        </div>

        <div className="tabs padded-tabs">
          <button className={`tab ${activeTab === 'members' ? 'active' : ''}`} onClick={() => setActiveTab('members')} type="button">
            <Users size={14} /> Thành viên
          </button>
          {canManageUsers && (
            <button className={`tab ${activeTab === 'create' ? 'active' : ''}`} onClick={() => setActiveTab('create')} type="button">
              <UserPlus size={14} /> Tạo user mới
            </button>
          )}
        </div>

        <div className="modal-body users-modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          {message && <div className="alert alert-success">{message}</div>}

          <div className="stats-grid user-stats-grid">
            <div className="stat-card">
              <div className="stat-label">Tổng số user</div>
              <div className="stat-value">{counts.total}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Admin</div>
              <div className="stat-value">{counts.admin}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Manager</div>
              <div className="stat-value">{counts.manager}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Viewer</div>
              <div className="stat-value">{counts.viewer}</div>
            </div>
          </div>

          {activeTab === 'members' && (
            <>
              <div className="users-toolbar">
                <input className="form-input" placeholder="Tìm theo tên, username, email, role..." value={query} onChange={(e) => setQuery(e.target.value)} />
              </div>

              {loading ? (
                <div className="spinner-wrap"><div className="spinner" /></div>
              ) : (
                <div className="user-list">
                  {filteredUsers.map((user) => {
                    const isEditing = editingId === user.id;
                    return (
                      <div className="user-card" key={user.id}>
                        <div className="user-card-head">
                          <div>
                            {isEditing ? (
                              <input className="form-input" value={editingDraft.display_name} onChange={(e) => setEditingDraft((prev) => ({ ...prev, display_name: e.target.value }))} />
                            ) : (
                              <div className="user-name">{user.display_name}</div>
                            )}
                            <div className="user-meta-line">ID #{user.id} · {user.created_at ? new Date(user.created_at).toLocaleString('vi-VN') : ''}</div>
                          </div>
                          <span className={`badge badge-role-${user.role}`}>{user.role}</span>
                        </div>

                        <div className="user-card-grid">
                          <div>
                            <div className="form-label">Username</div>
                            {isEditing ? (
                              <input className="form-input" value={editingDraft.username} onChange={(e) => setEditingDraft((prev) => ({ ...prev, username: e.target.value }))} />
                            ) : (
                              <div className="user-card-value">{user.username}</div>
                            )}
                          </div>
                          <div>
                            <div className="form-label">Email</div>
                            {isEditing ? (
                              <input className="form-input" value={editingDraft.email} onChange={(e) => setEditingDraft((prev) => ({ ...prev, email: e.target.value }))} />
                            ) : (
                              <div className="user-card-value">{user.email}</div>
                            )}
                          </div>
                          <div>
                            <div className="form-label">Role</div>
                            {isEditing ? (
                              <select className="form-select" value={editingDraft.role} onChange={(e) => setEditingDraft((prev) => ({ ...prev, role: e.target.value }))}>
                                <option value="viewer">Viewer</option>
                                <option value="manager">Manager</option>
                                <option value="admin">Admin</option>
                              </select>
                            ) : (
                              <div className="user-card-value">{user.role}</div>
                            )}
                          </div>
                        </div>

                        <div className="user-card-actions">
                          {canManageUsers ? (
                            <>
                              {isEditing ? (
                                <button className="btn btn-primary btn-sm" type="button" onClick={() => handleSaveEdit(user.id)} disabled={saving}>
                                  <Save size={14} /> Lưu
                                </button>
                              ) : (
                                <button className="btn btn-ghost btn-sm" type="button" onClick={() => startEdit(user)}>
                                  <Pencil size={14} /> Chỉnh sửa
                                </button>
                              )}
                              <button className="btn btn-ghost btn-sm" type="button" onClick={() => handleResetPassword(user)}>
                                <KeyRound size={14} /> Đổi pass
                              </button>
                              <button className="btn btn-danger btn-sm" type="button" onClick={() => handleDelete(user)}>
                                <Trash2 size={14} /> Xoá
                              </button>
                            </>
                          ) : (
                            <div className="readonly-note"><Eye size={14} /> Bạn chỉ có quyền xem danh sách thành viên.</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {filteredUsers.length === 0 && <div className="empty-state">Không tìm thấy user nào</div>}
                </div>
              )}
            </>
          )}

          {activeTab === 'create' && canManageUsers && (
            <form onSubmit={handleCreate} className="stack-gap-lg">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Tên hiển thị *</label>
                  <input className="form-input" value={form.display_name} onChange={(e) => setForm((prev) => ({ ...prev, display_name: e.target.value }))} placeholder="Nguyễn Văn A" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Username *</label>
                  <input className="form-input" value={form.username} onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))} placeholder="nguyenvana" required />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input className="form-input" type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} placeholder="email@example.com" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Mật khẩu *</label>
                  <input className="form-input" type="password" value={form.password} onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))} placeholder="Tối thiểu 6 ký tự" required />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Vai trò</label>
                <select className="form-select" value={form.role} onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}>
                  <option value="viewer">Viewer - Chỉ xem và comment</option>
                  <option value="manager">Manager - Tạo task, duyệt yêu cầu</option>
                  <option value="admin">Admin - Toàn quyền</option>
                </select>
                <div className="role-help-grid">
                  <div><Shield size={14} /> Admin: quản trị toàn bộ hệ thống.</div>
                  <div><UserPlus size={14} /> Manager: tạo task, duyệt request, không quản trị user.</div>
                  <div><Eye size={14} /> Viewer: chỉ xem task và bình luận.</div>
                </div>
              </div>

              <div className="modal-footer flush-footer">
                <button className="btn btn-ghost" type="button" onClick={onClose}>Đóng</button>
                <button className="btn btn-primary" type="submit" disabled={saving}>
                  <UserPlus size={15} /> {saving ? 'Đang tạo...' : 'Tạo User'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
